import { WaitForTransactionReceiptTimeoutError } from 'viem'

import { logger } from '@/lib/observability/logger'
import { SimulationError } from '@/services/simulate/simulate'

export interface TransactionErrorContext {
  chainId: number
  actionType?: string
  walletType: 'safe' | 'smartWallet' | 'eoa'
  txHash?: string
}

/** Sentry caps tag values at 200 chars; longer values are dropped as "invalid data". */
const SENTRY_TAG_MAX_LENGTH = 200

/**
 * Sanitizes an EVM revert reason for use as a Sentry *tag* value.
 *
 * Revert reasons from `eth_simulateV1` can carry newlines, control
 * characters, or raw decoded payloads. Sentry rejects such tag values ("invalid
 * data" / "invalid character" in the UI), so we strip control characters,
 * collapse whitespace, and truncate to the 200-char tag limit. The full, raw
 * reason is kept separately in `extra` (which has no such constraints).
 *
 * @returns the sanitized value, or `undefined` if nothing printable remains.
 */
function sanitizeSentryTagValue(value: string): string | undefined {
  const cleaned = Array.from(value, char => {
    const code = char.codePointAt(0) ?? 0
    // C0 controls (incl. newline/tab) and C1 controls (incl. DEL) → space.
    return code < 0x20 || (code >= 0x7f && code <= 0x9f) ? ' ' : char
  })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()

  if (!cleaned) return undefined

  return cleaned.length > SENTRY_TAG_MAX_LENGTH
    ? `${cleaned.slice(0, SENTRY_TAG_MAX_LENGTH - 3)}...`
    : cleaned
}

/**
 * Address-shaped (0x + 40 hex) and hash-shaped (0x + 64 hex) blobs collapse to
 * `0x` — an embedded address/tx-hash would otherwise split one logical revert
 * into per-address issues. Shorter blobs (custom-error selectors, …) keep their
 * first 4 bytes so distinct errors still fingerprint differently.
 */
const maskHexBlob = (match: string): string =>
  match.length === 42 || match.length === 66 ? '0x' : match.slice(0, 10)

/**
 * Normalizes a revert reason into a bounded fingerprint token: hex blobs keep
 * their 4-byte selector except address/hash-shaped ones (`0x…` → `0x`), and
 * digits are masked (`#`) so `unknown` reverts group by shape — bare
 * `execution reverted`, arithmetic errors and distinct custom errors don't
 * collapse into one Sentry issue. Expected categories stay grouped per category
 * and never reach this helper.
 */
function fingerprintReasonToken(reason: string | undefined): string {
  const cleaned = (reason ?? '')
    .toLowerCase()
    // Single pass: masking hex first then digits would turn the `0x` placeholder
    // itself into `#x`.
    .replace(/0x[0-9a-f]+|\d+/g, match => (match.startsWith('0x') ? maskHexBlob(match) : '#'))
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 64)

  return cleaned || 'none'
}

/**
 * Logs transaction errors with appropriate severity and context.
 * Handles SimulationError (fatal only for unexplained on-chain reverts; expected
 * reverts and simulate-API failures are error level) and generic errors (error level).
 *
 * @param error - The caught error
 * @param context - Transaction context for logging
 * @returns The error message string for display
 */
export function logTransactionError(error: unknown, context: TransactionErrorContext): string {
  const { chainId, actionType, walletType, txHash } = context

  if (error instanceof SimulationError) {
    return logSimulationError(error, context)
  }

  if (error instanceof WaitForTransactionReceiptTimeoutError) {
    logger.error(error, {
      tags: {
        transaction_failed: 'true',
        timeout_type: 'receipt_polling',
        chain_id: String(chainId),
        ...(actionType ? { action_type: actionType } : {}),
        wallet_type: walletType
      },
      extra: {
        chainId,
        txHash
      },
      fingerprint: [
        'WaitForTransactionReceiptTimeoutError',
        String(chainId),
        actionType ?? 'unknown'
      ]
    })
    return error.message
  }

  const normalizedError = error instanceof Error ? error : new Error(String(error))

  const isRpcTimeout =
    normalizedError.message.toLowerCase().includes('timeout') ||
    normalizedError.message.toLowerCase().includes('timed out') ||
    normalizedError.message.toLowerCase().includes('etimedout')

  logger.error(normalizedError, {
    tags: {
      transaction_failed: 'true',
      chain_id: String(chainId),
      ...(actionType ? { action_type: actionType } : {}),
      wallet_type: walletType,
      ...(isRpcTimeout ? { timeout_type: 'rpc_request' } : {})
    },
    extra: {
      chainId,
      txHash
    }
  })

  return normalizedError.message
}

/**
 * Logs a SimulationError with its simulation tags, extras and fingerprint.
 *
 * Severity rule: only unexplained on-chain reverts ('unknown' category — bare
 * `execution reverted`, arithmetic overflows, …) are fatal since they may hide a
 * bug. Expected reverts (user-actionable causes like low balance or an
 * already-set authorization) and simulation_api_call infra failures (502/5xx,
 * network) are error-level: captured for reporting but they don't page the
 * on-call.
 */
function logSimulationError(error: SimulationError, context: TransactionErrorContext): string {
  const { actionType, walletType } = context

  const simulationCode = error.details?.code ?? 'unknown_code'
  const simulationReason = error.details?.reason
  // Tags are constrained; the full reason is preserved in `extra` below.
  const simulationReasonTag = simulationReason
    ? sanitizeSentryTagValue(simulationReason)
    : undefined

  const logContext = {
    tags: {
      simulate_failed: 'true',
      simulate_code: simulationCode,
      // Always emit the tag so Sentry's "by reason" widget has no blank
      // bucket: infra failures (simulation_api_call) and undecoded reverts
      // carry no on-chain reason, so they land in an explicit 'none' group.
      // Use the sanitized tag value (Sentry tag constraints) when present.
      simulate_reason: simulationReasonTag || 'none',
      ...(error.revertCategory !== undefined
        ? { simulate_revert_category: error.revertCategory }
        : {}),
      failure_type: error.failureType,
      chain_id: error.chainId.toString(),
      action_type: actionType ?? 'unknown',
      wallet_type: walletType,
      tx_count: error.transactions.length.toString(),
      ...(error.details?.httpStatus
        ? { simulate_http_status: error.details.httpStatus.toString() }
        : {})
    },
    extra: {
      transactions: error.transactions,
      asset_changes: error.details?.assetChanges,
      simulate_error_message: error.details?.error,
      // Full, unsanitized revert reason — `extra` has no tag-value constraints.
      ...(simulationReason ? { simulate_reason: simulationReason } : {})
    },
    fingerprint: [
      'SimulationError',
      error.failureType,
      simulationCode,
      ...(error.revertCategory ? [error.revertCategory] : []),
      ...(error.revertCategory === 'unknown' ? [fingerprintReasonToken(simulationReason)] : [])
    ]
  }

  // A slippage-limit violation is market drift past the quoted tolerance, not a potential bug.
  const isLimitViolation = error.details?.code === 'CONSUMER_LIMIT_VIOLATION'
  if (error.failureType === 'simulation' && !error.isExpectedRevert && !isLimitViolation) {
    logger.fatal(error, logContext)
  } else {
    logger.error(error, logContext)
  }
  return error.message
}
